import {
  useGetPartiesSuspense,
  usePostParties,
} from 'api/payments/generated/fragmentPaymentsAPI';
import {
  PaymentViewHeader,
  PaymentViewLayout,
  PaymentViewLoading,
} from 'components/Layout/PaymentViewLayout';
import { useDocumentTitle } from 'hooks/useDocumentTitle';
import { capitalize } from 'lodash-es';
import React, { Suspense, useCallback, useMemo, useState } from 'react';
import { makePath } from 'utils/routes';
import { useQueryClient } from '@tanstack/react-query';

import { FormButtons } from '@fragment/ui/src/components/Button/ButtonGroup/FormButtons';
import { PrimaryButtonV3 } from '@fragment/ui/src/components/Button/PrimaryButtonV3/PrimaryButtonV3';
import { ErrorText } from '@fragment/ui/src/components/ErrorText';
import { Modal } from '@fragment/ui/src/components/Modal/Modal';
import { ModalField } from '@fragment/ui/src/components/Modal/ModalField';
import { ModalTextInput } from '@fragment/ui/src/components/Modal/ModalTextInput';
import type { ColumnDefinition } from '@fragment/ui/src/components/Table';
import { NavTableV2 } from '@fragment/ui/src/components/Table/NavTableV2';
import { TabsV2 } from '@fragment/ui/src/components/Tabs/TabsV2';

import { PLATFORM_ENTITY } from './constants';

type PartyRow = {
  id: string;
  externalId: string;
  counterPartyType?: string;
};

const emptyValue = {
  'External ID': 'No Parties Yet',
  Verified: 'N/A',
};

const schema: ColumnDefinition<PartyRow>[] = [
  {
    title: 'External ID',
    colspan: 'col-span-2',
    key: 'externalId',
  },
  {
    title: 'Type',
    justify: 'right',
    format: (row: PartyRow) => {
      if (row.externalId === PLATFORM_ENTITY) {
        return <span className="text-main">Platform</span>;
      }

      return (
        <span className="truncate text-main-500">
          {row.counterPartyType ? capitalize(row.counterPartyType) : '-'}
        </span>
      );
    },
  },
];

type Props = {
  workspaceId: string;
};

const PartiesListPageContent = ({ workspaceId }: Props): JSX.Element => {
  useDocumentTitle('Parties');

  const { data } = useGetPartiesSuspense();
  // Note: Suspense handles loading state, error boundary handles errors
  const queryClient = useQueryClient();
  const createPartyMutation = usePostParties();

  const [showCreateModal, setShowCreateModal] = useState(false);
  const [externalId, setExternalId] = useState('');
  const [typeInput, setTypeInput] = useState('');
  const [createError, setCreateError] = useState<string | undefined>();
  const [selectedType, setSelectedType] = useState<string>('all');

  const parties: PartyRow[] = useMemo(
    () =>
      data.data.map((party) => ({
        id: party.externalId, // Using externalId as id since there's no separate id field
        externalId: party.externalId,
        counterPartyType: party.counterPartyType,
      })),
    [data.data]
  );

  // Extract unique types from all parties and create filter options
  const typeFilterOptions = useMemo(() => {
    const typeSet = new Set<string>();
    parties.forEach((party) => {
      if (party.counterPartyType) {
        typeSet.add(party.counterPartyType);
      }
    });
    const types = Array.from(typeSet).sort();

    return [
      { value: 'all' as const, label: 'All' },
      ...types.map((type) => ({ value: type, label: capitalize(type) })),
    ];
  }, [parties]);

  // Filter parties by selected type
  const filteredParties = useMemo(() => {
    if (selectedType === 'all') {
      return parties;
    }
    return parties.filter((party) => party.counterPartyType === selectedType);
  }, [parties, selectedType]);

  const getPath = useCallback(
    (party: PartyRow) =>
      makePath(['w', workspaceId, 'payments', 'parties', party.externalId], {
        absolute: true,
      }),
    [workspaceId]
  );

  const handleCreateParty = useCallback(
    async (e: React.FormEvent) => {
      e.preventDefault();
      setCreateError(undefined);
      try {
        await createPartyMutation.mutateAsync({
          data: {
            externalId,
            type: typeInput.trim() || undefined,
          },
        });

        // Refetch the parties list
        await queryClient.invalidateQueries({ queryKey: ['/parties'] });

        // Close modal and reset form
        setShowCreateModal(false);
        setExternalId('');
        setTypeInput('');
      } catch (err) {
        setCreateError(
          err instanceof Error ? err.message : 'Failed to create party'
        );
      }
    },
    [externalId, typeInput, createPartyMutation, queryClient]
  );

  const handleCloseModal = useCallback(() => {
    setShowCreateModal(false);
    setExternalId('');
    setTypeInput('');
    setCreateError(undefined);
  }, []);

  return (
    <PaymentViewLayout header={<PaymentViewHeader>Parties</PaymentViewHeader>}>
      <div className="flex flex-col min-h-f1 w-full h-full">
        <div className="flex flex-row justify-between">
          <TabsV2
            options={typeFilterOptions}
            selectedValue={selectedType}
            onClick={setSelectedType}
          />
          <div>
            <PrimaryButtonV3 onClick={() => setShowCreateModal(true)}>
              +
            </PrimaryButtonV3>
          </div>
        </div>
        <NavTableV2
          textSize="md"
          data={filteredParties}
          emptyValue={emptyValue}
          getPath={getPath}
          schema={schema}
          showHeader={false}
        />
        {showCreateModal && (
          <Modal
            onClose={handleCloseModal}
            header="Create Party"
            onSubmit={handleCreateParty}
            body={
              <div className="flex flex-col space-y-f2">
                <ModalField
                  label={<label htmlFor="external-id">External ID</label>}
                >
                  <ModalTextInput
                    id="external-id"
                    value={externalId}
                    onChange={(e) => setExternalId(e.target.value)}
                    placeholder="Enter external ID"
                  />
                </ModalField>
                <ModalField
                  label={<label htmlFor="type">Type (optional)</label>}
                >
                  <ModalTextInput
                    id="type"
                    value={typeInput}
                    onChange={(e) => setTypeInput(e.target.value)}
                    placeholder="e.g., buyer, seller"
                  />
                </ModalField>
              </div>
            }
            footer={
              <div className="flex flex-col gap-y-f2 w-full">
                {createError && <ErrorText>{createError}</ErrorText>}
                <div className="flex flex-row justify-between">
                  <FormButtons
                    type="secondary"
                    cancelButtonLabel="Cancel"
                    onCancel={handleCloseModal}
                    submitButtonLabel={
                      createPartyMutation.isPending ? 'Creating...' : 'Create'
                    }
                    disabled={createPartyMutation.isPending || !externalId}
                  />
                </div>
              </div>
            }
          />
        )}
      </div>
    </PaymentViewLayout>
  );
};

export const PartiesListPage = ({ workspaceId }: Props): JSX.Element => (
  <Suspense fallback={<PaymentViewLoading message="Loading parties..." />}>
    <PartiesListPageContent workspaceId={workspaceId} />
  </Suspense>
);
